import type { ThingType } from '../../../models/thing.models';

class GenuineSrtElsEntry {
  constructor(
    readonly serialNumber?: string,
    readonly sku?: number,
    readonly mpn?: string,
    readonly revision?: string,
    readonly devEui?: string,
    readonly productionDate?: string,
    readonly appEui?: string,
    readonly appKey?: string,
    readonly firmware?: string,
  ) {}
}
const optionalProperties = ['firmware'];
export type SrtGenuineDeviceElsEntry = Required<GenuineSrtElsEntry>;
const GENUINE_SRT_ELS_MANDATORY_KEYS = Object.keys(new GenuineSrtElsEntry()).filter((k) => !optionalProperties.includes(k));

class TestSrtElsEntry {
  constructor(
    readonly serialNumber?: string,
    readonly sku?: number,
    readonly mpn?: string,
    readonly revision?: string,
    readonly devEui?: string,
    readonly productionDate?: string,
    readonly customerPrefix?: 'io.beyonnex.smartheating.srt.mock',
    readonly firmware?: string,
  ) {}
}
export type SrtTestDeviceElsEntry = Required<TestSrtElsEntry>;
const TEST_SRT_ELS_MANDATORY_KEYS = Object.keys(new TestSrtElsEntry()).filter((k) => !optionalProperties.includes(k));

export type SrtDeviceElsEntry = SrtGenuineDeviceElsEntry | SrtTestDeviceElsEntry;

export type SrtElsDocument = SrtDeviceElsEntry[];

export function isSrtGenuineDeviceElsEntry(entry: object): entry is SrtGenuineDeviceElsEntry {
  return GENUINE_SRT_ELS_MANDATORY_KEYS.reduce((p, c) => p && c in entry, true);
}

export function isSrtTestDeviceElsEntry(entry: { customerPrefix?: unknown }): entry is SrtTestDeviceElsEntry {
  return (
    TEST_SRT_ELS_MANDATORY_KEYS.reduce((p, c) => p && c in entry, true) && entry.customerPrefix === 'io.beyonnex.smartheating.srt.mock'
  );
}

export function isSrtElsDocument(json: unknown): json is SrtElsDocument {
  if (!Array.isArray(json)) {
    return false;
  }

  return json.reduce((p, c) => p && (isSrtGenuineDeviceElsEntry(c) || isSrtTestDeviceElsEntry(c)), true);
}

const DRAGINO_MODELS = ['D20', 'D22', 'D23'] as const;
class TemperatureSensorElsEntry {
  constructor(
    readonly serialNumber?: string,
    readonly devEui?: string,
    readonly appKey?: string,
    readonly appEui?: string,
    readonly model?: (typeof DRAGINO_MODELS)[number],
    readonly productKey?: 'lsn50v2',
  ) {}
}
export type TemperatureSensorDeviceElsEntry = Required<TemperatureSensorElsEntry>;
const TEMPERATURE_SENSOR_ELS_KEYS = Object.keys(new TemperatureSensorElsEntry());

export function isTemperatureSensorElsEntry(entry: {
  productKey?: unknown;
  model?: (typeof DRAGINO_MODELS)[number];
}): entry is TemperatureSensorDeviceElsEntry {
  return (
    TEMPERATURE_SENSOR_ELS_KEYS.reduce((p, c) => p && c in entry, true) &&
    !!entry.model &&
    DRAGINO_MODELS.includes(entry.model) &&
    entry.productKey === 'lsn50v2'
  );
}

export type TemperatureSensorElsDocument = TemperatureSensorDeviceElsEntry[];

export function isTemperatureElsDocument(json: unknown): json is TemperatureSensorElsDocument {
  if (!Array.isArray(json)) {
    return false;
  }

  return json.reduce((p, c) => p && isTemperatureSensorElsEntry(c), true);
}

class PulseCounterElsEntry {
  constructor(
    readonly serialNumber?: string,
    readonly devEui?: string,
    readonly appKey?: string,
    readonly appEui?: string,
    readonly productKey?: 'jooby-omni-4pu-204',
    readonly customerPrefix?: string,
    readonly meterType?: 'gas' | 'water' | 'electricity',
  ) {}
}
const pulseCounterOptionalProperties = ['customerPrefix', 'meterType'];
export type PulseCounterDeviceElsEntry = Required<Omit<PulseCounterElsEntry, 'customerPrefix' | 'meterType'>> &
  Pick<PulseCounterElsEntry, 'customerPrefix' | 'meterType'>;
const PULSE_COUNTER_ELS_MANDATORY_KEYS = Object.keys(new PulseCounterElsEntry()).filter((k) => !pulseCounterOptionalProperties.includes(k));

export function isPulseCounterElsEntry(entry: { productKey?: unknown }): entry is PulseCounterDeviceElsEntry {
  return PULSE_COUNTER_ELS_MANDATORY_KEYS.reduce((p, c) => p && c in entry, true) && entry.productKey === 'jooby-omni-4pu-204';
}

export type PulseCounterElsDocument = PulseCounterDeviceElsEntry[];

export function isPulseCounterElsDocument(json: unknown): json is PulseCounterElsDocument {
  return Array.isArray(json) && json.reduce((p, c) => p && isPulseCounterElsEntry(c), true);
}

export const ELS_JOB_URL_STORAGE_KEY = 'TADO_DRAGINO_ELS_JOB_URL';

// A pulse counter is the medium-agnostic device; the import creates one PULSE_GAS_METER thing per channel.
export type ElsDeviceType = Extract<ThingType, 'SMART_RADIATOR_THERMOSTAT' | 'TEMPERATURE_SENSOR'> | 'PULSE_COUNTER';

export function resolveElsDeviceType(json: unknown): ElsDeviceType | null {
  if (!Array.isArray(json) || json.length === 0) {
    return null;
  }
  if (isSrtElsDocument(json)) {
    return 'SMART_RADIATOR_THERMOSTAT';
  }
  if (isTemperatureElsDocument(json)) {
    return 'TEMPERATURE_SENSOR';
  }
  if (isPulseCounterElsDocument(json)) {
    return 'PULSE_COUNTER';
  }
  return null;
}

export interface ElsImportResult {
  devEUI: string;
  /** Absent when no thing was created — only a CREATED or EXISTS result links to a device. */
  thingId?: string;
  status: 'CREATED' | 'EXISTS' | 'PENDING' | 'FAILED';
  error: string | null;
  /** 1..4 for a pulse counter, which fans out to one thing per channel; null for SRT and Dragino. */
  channel?: number | null;
}

export interface ElsJobStatistics {
  success: number;
  failed: number;
  pending: number;
  existing: number;
  importResults: ElsImportResult[];
}
